import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import ActiveOrdersPage from "src/pages/ActiveOrdersPage";
import CompletedOrdersPage from "src/pages/CompletedOrdersPage";
import SearchBar from "src/components/common/SearchBar";
import OrderTypeFilterButtons from "src/components/common/OrderTypeFilterButtons";
import { OrderStatusEnum, OrderTypeEnum } from "src/enums/order.enum";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_COMPLETED_ORDERS } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { ReportDateRangePicker } from "src/features/Reports/components/ReportDateRangePicker";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportTabs } from "src/features/Reports/components/ReportTabs";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { CustomerOrder } from "src/interfaces/order.interface";
import { CustomerOrderEdge } from "src/interfaces/orders-props.interface";
import { failureToast, successToast } from "src/utils/toasts";

type OrdersTab = "active" | "completed";

const ORDERS_TABS = [
  { id: "active", label: "Active" },
  { id: "completed", label: "Completed" },
];

const EXPORT_PAGE_SIZE = 100;
const MAX_EXPORT_PAGES = 100;

function formatDateTime(value: number | null | undefined): string {
  if (value == null) return "-";
  return new Date(value).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDateForFilename(iso: string): string {
  return iso.slice(0, 10).replace(/-/g, "");
}

function orderTypeLabel(orderType: string | undefined): string {
  if (!orderType) return "-";
  return orderType.replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function shortOrderId(correlationId: string | undefined): string {
  return correlationId ? `#${correlationId.slice(-5)}` : "-";
}

function orderMatchesSearch(order: CustomerOrder, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    order.customerFirstName?.toLowerCase().includes(q) ||
    order.customerLastName?.toLowerCase().includes(q) ||
    (order.customerEmail ?? "").toLowerCase().includes(q) ||
    (order.customerPhoneNumber ?? "").includes(query.trim()) ||
    (order.correlationId ?? "").toLowerCase().includes(q)
  );
}

function isVoidedOrder(order: CustomerOrder): boolean {
  return order.orderStatus === OrderStatusEnum.VOIDED || order.voidedAt != null;
}

function writeText(doc: jsPDF, text: string, x: number, y: number, maxWidth: number) {
  doc.text(String(text ?? "-"), x, y, { maxWidth });
}

function buildCompletedOrdersPdf(orders: CustomerOrder[], range: { from: string; to: string }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const rowHeight = 24;
  const headerY = 88;
  const columns = [
    { label: "Date", x: 36, width: 112 },
    { label: "Order", x: 158, width: 60 },
    { label: "Type", x: 228, width: 86 },
    { label: "Channel", x: 324, width: 116 },
    { label: "Status", x: 450, width: 86 },
    { label: "Payment", x: 546, width: 96 },
    { label: "Total", x: 652, width: 74 },
    { label: "Accumulated Total", x: 736, width: 100 },
  ];

  const addPageHeader = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Completed Orders", margin, 36);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `${new Date(range.from).toLocaleDateString()} - ${new Date(range.to).toLocaleDateString()} · ${orders.length} orders`,
      margin,
      54
    );
    doc.text(`Generated ${new Date().toLocaleString()}`, pageWidth - margin, 54, { align: "right" });
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, 68, pageWidth - margin, 68);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    columns.forEach((column) => doc.text(column.label, column.x, headerY));
    doc.line(margin, headerY + 8, pageWidth - margin, headerY + 8);
    doc.setFont("helvetica", "normal");
  };

  addPageHeader();
  let y = headerY + 28;
  doc.setFontSize(8);

  let accumulatedTotalCents = 0;

  orders.forEach((order) => {
    if (y > pageHeight - margin) {
      doc.addPage();
      addPageHeader();
      doc.setFontSize(8);
      y = headerY + 28;
    }

    const paymentStatus = order.payment?.paymentStatus || (order.paid ? "PAID" : "UNPAID");
    if (!isVoidedOrder(order)) {
      accumulatedTotalCents += order.totalCents ?? 0;
    }
    const row = [
      formatDateTime(order.createdAt),
      shortOrderId(order.correlationId),
      orderTypeLabel(order.orderType),
      order.channelName || "-",
      order.orderStatus || "-",
      paymentStatus,
      order.totalCents != null ? formatCentsToDollarsWithDollarSign(order.totalCents) : "-",
      formatCentsToDollarsWithDollarSign(accumulatedTotalCents),
    ];

    columns.forEach((column, index) => writeText(doc, row[index], column.x, y, column.width));
    doc.setDrawColor(241, 245, 249);
    doc.line(margin, y + 8, pageWidth - margin, y + 8);
    y += rowHeight;
  });

  doc.save(`completed-orders-${formatDateForFilename(range.from)}-${formatDateForFilename(range.to)}.pdf`);
}

const OrdersPage = () => {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get("tab") as OrdersTab) || "active";
  const activeTab = tab === "completed" ? "completed" : "active";

  const [searchQuery, setSearchQuery] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState<OrderTypeEnum | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last30");
  const [dateRange, setDateRange] = useState(() => getReportDateRange("last30"));
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);

  const handleDatePresetChange = (preset: ReportDatePreset, from: string, to: string) => {
    setDatePreset(preset);
    setDateRange({ preset, from, to });
    if (preset === "custom") {
      setCustomFrom(from);
      setCustomTo(to);
    } else {
      setCustomFrom(null);
      setCustomTo(null);
    }
  };

  const setTab = (t: OrdersTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", t);
      return next;
    });
  };

  const handleExportCompletedOrdersPdf = async () => {
    if (!hasRestaurant?.correlationId) return;
    setIsExportingPdf(true);
    try {
      const allOrders: CustomerOrder[] = [];
      let after: string | undefined;
      let pageCount = 0;
      let hasNextPage = true;

      while (hasNextPage && pageCount < MAX_EXPORT_PAGES) {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_COMPLETED_ORDERS,
          variables: {
            storeCorrelationId: hasRestaurant.correlationId,
            first: EXPORT_PAGE_SIZE,
            after,
            startTimeInEpochMilli: new Date(dateRange.from).getTime(),
            endTimeInEpochMilli: new Date(dateRange.to).getTime(),
            excludeOrderStatus: "OPEN",
            orderType: orderTypeFilter ?? null,
          },
        });

        if (data?.errors?.length) throw data.errors[0];

        const connection = data?.data?.customerOrdersByStoreCorrelationId;
        const edges: CustomerOrderEdge[] = connection?.edges ?? [];
        allOrders.push(...edges.map((edge) => edge.node).filter(Boolean));
        hasNextPage = Boolean(connection?.pageInfo?.hasNextPage);
        after = connection?.pageInfo?.endCursor || undefined;
        pageCount += 1;
      }

      const filteredOrders = allOrders.filter((order) => orderMatchesSearch(order, searchQuery));
      if (filteredOrders.length === 0) {
        failureToast("No completed orders found for this export.");
        return;
      }

      buildCompletedOrdersPdf(filteredOrders, dateRange);
      successToast("Completed orders PDF downloaded.");
    } catch (error) {
      console.error("Error exporting completed orders PDF:", error);
      failureToast("Failed to export completed orders PDF.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <ReportPageLayout
      compactTop
      fillHeight
      toolbarMarginTop={0}
      toolbarMarginBottom={12}
      toolbar={
        <div className="flex flex-col gap-3 w-full min-w-0 md:flex-row md:items-center md:justify-between md:gap-4">
          <ReportTabs
            tabs={ORDERS_TABS}
            activeTabId={activeTab}
            onSelect={(id) => setTab(id as OrdersTab)}
            inline
          />
          <div className="flex flex-col gap-3 w-full min-w-0 md:flex-row md:items-center md:gap-3 md:w-auto md:shrink-0 md:ml-auto">
            {activeTab === "completed" && (
              <>
                <div className="w-full min-w-0 order-1 md:w-auto md:shrink-0">
                  <ReportDateRangePicker
                    value={datePreset}
                    onChange={handleDatePresetChange}
                    customFrom={datePreset === "custom" ? customFrom : undefined}
                    customTo={datePreset === "custom" ? customTo : undefined}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleExportCompletedOrdersPdf}
                  disabled={isExportingPdf || !hasRestaurant?.correlationId}
                  className="h-10 w-full order-4 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm font-medium text-[#374151] transition-colors hover:border-[#d1d5db] hover:bg-[#fafafa] disabled:cursor-not-allowed disabled:opacity-50 md:order-4 md:w-auto md:shrink-0"
                >
                  {isExportingPdf ? "Exporting..." : "Export PDF"}
                </button>
              </>
            )}
            <div className="w-full min-w-0 order-2 md:w-96 md:shrink-0">
              <SearchBar
                placeholder="Search by ID, phone, name, email"
                value={searchQuery}
                onChange={setSearchQuery}
                className="w-full"
              />
            </div>
            <div className="shrink-0 order-3 w-full min-w-0 md:w-auto">
              <OrderTypeFilterButtons value={orderTypeFilter} onChange={setOrderTypeFilter} />
            </div>
          </div>
        </div>
      }
    >
      <div className="flex-1 min-h-0 flex flex-col min-w-0">
        {activeTab === "active" && (
          <ActiveOrdersPage
            hideTitle
            controlledSearchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            controlledOrderTypeFilter={orderTypeFilter}
            onOrderTypeFilterChange={setOrderTypeFilter}
          />
        )}
        {activeTab === "completed" && (
          <CompletedOrdersPage
            hideTitle
            controlledSearchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            controlledOrderTypeFilter={orderTypeFilter}
            onOrderTypeFilterChange={setOrderTypeFilter}
            dateRangeFrom={dateRange.from}
            dateRangeTo={dateRange.to}
          />
        )}
      </div>
    </ReportPageLayout>
  );
};

export default OrdersPage;
